import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Award, Activity, CheckCircle, AlertCircle, Info, User, Phone, MapPin } from 'lucide-react';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';

const Session = () => {
  const { user } = useAuth();
  const [sessionData, setSessionData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSessionInfo = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_API_URL}api/get-swimming-records`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
        
        const data = await response.json();
        if (data.status === 'success') {
          const userApps = data.data.filter(app => app.student_id == user.id);
          
          if (userApps.length > 0) {
            // Get the most recent application
            setSessionData(userApps[userApps.length - 1]);
          }
        }
      } catch (error) {
        console.error('Error fetching session:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSessionInfo();
  }, [user]);

  return (
    <div className="dashboard-container">
      <Header />
      <div className="dashboard-wrapper">
        <Sidebar activeItem="session" />
        <div className="flex-grow-1 dashboard-content">
          <h2 className="page-title">Session Details</h2>
          
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : !sessionData ? (
             <div className="alert border-0 d-flex align-items-center gap-3 p-4" style={{ backgroundColor: '#e8f4fd', borderRadius: '16px' }}>
                <div style={{ color: '#0d6efd' }}><AlertCircle size={28} /></div>
                <div>
                  <strong className="d-block text-dark mb-1 fs-5">No Active Session Found</strong>
                  <span className="text-secondary">You haven't submitted any applications for a training session yet.</span>
                </div>
              </div>
          ) : (
            <div className="session-details-container">
              {/* Current Session Card */}
              <div className="session-card">
                <div className="session-card-header">
                  <div className="session-card-icon">
                    <Calendar size={24} />
                  </div>
                  <h3 className="session-card-title">Current Session</h3>
                </div>
                
                <div className="session-info-item">
                  <span className="session-info-label">
                    <Calendar size={16} />
                    Session Year
                  </span>
                  <span className="session-info-value">{sessionData.session?.session || 'N/A'}</span>
                </div>
                
                <div className="session-info-item">
                  <span className="session-info-label">
                    <Clock size={16} />
                    Shift
                  </span>
                  <span className="session-info-value">{sessionData.shift?.shift_name || 'N/A'}</span>
                </div>
                
                <div className="session-info-item">
                  <span className="session-info-label">
                    <Clock size={16} />
                    Time Schedule
                  </span>
                  <span className="session-info-value">{sessionData.time_slot?.time || 'N/A'}</span>
                </div>
                
                <div className="session-info-item">
                  <span className="session-info-label">
                    <Calendar size={16} />
                    Start Date
                  </span>
                  <span className="session-info-value">{sessionData.registration_date ? new Date(sessionData.registration_date).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A'}</span>
                </div>
                
                <div className="session-info-item">
                  <span className="session-info-label">
                    <CheckCircle size={16} />
                    Registration No
                  </span>
                  <span className="session-info-value">{`BLG-${sessionData.session?.session}-${sessionData.registration_no?.replace('BLG', '')}` || 'Pending'}</span>
                </div>
              </div>
              
              {/* Trainee Details Card */}
              <div className="session-card">
                <div className="session-card-header">
                  <div className="session-card-icon" style={{ backgroundColor: '#e2edff', color: '#0d6efd' }}>
                    <User size={24} />
                  </div>
                  <h3 className="session-card-title">Trainee Profile</h3>
                </div>
                
                <div className="session-info-item">
                  <span className="session-info-label">
                    <User size={16} />
                    Full Name
                  </span>
                  <span className="session-info-value">{sessionData.application_name}</span>
                </div>
                
                <div className="session-info-item">
                  <span className="session-info-label">
                    <User size={16} />
                    Father's Name
                  </span>
                  <span className="session-info-value">{sessionData.name_of_father}</span>
                </div>
                
                <div className="session-info-item">
                  <span className="session-info-label">
                    <Award size={16} />
                    Category
                  </span>
                  <span className="session-info-value text-end" style={{ wordBreak: 'break-word', maxWidth: '50%' }}>
                    {sessionData.category?.category || 'N/A'}
                  </span>
                </div>
                
                <div className="session-info-item">
                  <span className="session-info-label">
                    <Phone size={16} />
                    Mobile No
                  </span>
                  <span className="session-info-value">{sessionData.mobile_no}</span>
                </div>
                
                <div className="session-info-item">
                  <span className="session-info-label">
                    <MapPin size={16} />
                    Ward No
                  </span>
                  <span className="session-info-value">{sessionData.ward_no}</span>
                </div>
                
                <div className="session-info-item">
                  <span className="session-info-label">
                    <MapPin size={16} />
                    Address
                  </span>
                  <span className="session-info-value text-end" style={{ wordBreak: 'break-word', maxWidth: '50%' }}>
                    {sessionData.residential_address}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Session;
